import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import { ILatestGradeSyncEvent, GradeSyncEventsByPair } from '../types';
import useGradesTableStore from '../store/useGradesTableStore';
import { pendingSyncKey } from '../store/createGradesTableStore';

const POLL_INTERVAL_MS = 10_000;

const reshape = (events: ILatestGradeSyncEvent[]): GradeSyncEventsByPair => {
  const out: GradeSyncEventsByPair = {};
  events.forEach((event) => {
    if (!out[event.assignment_id]) out[event.assignment_id] = {};
    out[event.assignment_id][event.student_id] = event;
  });
  return out;
};

const useGradeSyncEvents = () => {
  const assignments = useGradesTableStore(state => state.assignments);
  const students = useGradesTableStore(state => state.students);
  const stafferId = useGradesTableStore(state => state.stafferId);
  const programId = useGradesTableStore(state => state.programId);
  const pendingSyncs = useGradesTableStore(state => state.pendingSyncs);
  const setStore = useGradesTableStore(state => state.setStore);
  const clearSyncPending = useGradesTableStore(state => state.clearSyncPending);

  const hasPending = Object.keys(pendingSyncs).length > 0;

  const assignmentIds = useMemo(() => assignments.map(a => a.id), [assignments]);
  const studentIds = useMemo(() => students.map(s => s.id), [students]);

  const query = useQuery(
    ['GradeSyncEvents', stafferId, programId, assignmentIds, studentIds],
    () => axios
      .get(Routes.plato_api_latest_grade_sync_lms_sync_events_path({
        for_assignments: assignmentIds,
        for_students: studentIds,
      }))
      .then(res => res.data.data as ILatestGradeSyncEvent[]),
    {
      enabled: assignmentIds.length > 0 && studentIds.length > 0,
      refetchInterval: hasPending ? POLL_INTERVAL_MS : false,
    },
  );

  useEffect(() => {
    if (query.isFetching || !query.data) return;

    const reshaped = reshape(query.data);
    setStore({ gradeSyncEvents: reshaped });

    // Clear pending entries that have a newer event recorded.
    Object.entries(pendingSyncs).forEach(([key, clickedAt]) => {
      const [assignmentIdStr, studentIdStr] = key.split(':');
      const assignmentId = Number(assignmentIdStr);
      const studentId = Number(studentIdStr);
      const event = reshaped[assignmentId]?.[studentId];

      if (event?.last_attempt_at && new Date(event.last_attempt_at).getTime() > clickedAt) {
        clearSyncPending(assignmentId, studentId);
      }
    });
  }, [query.data, pendingSyncs, setStore, clearSyncPending]);

  return query;
};

export { pendingSyncKey };
export default useGradeSyncEvents;
